/*
 * Copyright 2021, Offchain Labs, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *    http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/* eslint-env node */
'use strict';
Object.defineProperty(exports, "__esModule", { value: true });
exports.AdminErc20Bridger = exports.Erc20Bridger = void 0;
const abi_1 = require("@ethersproject/abi");
const constants_1 = require("@ethersproject/constants");
const logger_1 = require("@ethersproject/logger");
const ethers_1 = require("ethers");
const L1GatewayRouter__factory_1 = require("../abi/factories/L1GatewayRouter__factory");
const L2GatewayRouter__factory_1 = require("../abi/factories/L2GatewayRouter__factory");
const L1ERC20Gateway__factory_1 = require("../abi/factories/L1ERC20Gateway__factory");
const L1WethGateway__factory_1 = require("../abi/factories/L1WethGateway__factory");
const L2ArbitrumGateway__factory_1 = require("../abi/factories/L2ArbitrumGateway__factory");
const ERC20__factory_1 = require("../abi/factories/ERC20__factory");
const L2GatewayToken__factory_1 = require("../abi/factories/L2GatewayToken__factory");
const ICustomToken__factory_1 = require("../abi/factories/ICustomToken__factory");
const IArbToken__factory_1 = require("../abi/factories/IArbToken__factory");
const L2CustomGateway__factory_1 = require("../abi/factories/L2CustomGateway__factory");
const L1ToL2MessageGasEstimator_1 = require("../message/L1ToL2MessageGasEstimator");
const signerOrProvider_1 = require("../dataEntities/signerOrProvider");
const errors_1 = require("../dataEntities/errors");
const constants_2 = require("../dataEntities/constants");
const eventFetcher_1 = require("../utils/eventFetcher");
const assetBridger_1 = require("./assetBridger");
const L1Transaction_1 = require("../message/L1Transaction");
const L2Transaction_1 = require("../message/L2Transaction");
const lib_1 = require("../utils/lib");
/**
 * Bridger for moving ERC20 tokens back and forth betwen L1 to L2
 */
class Erc20Bridger extends assetBridger_1.AssetBridger {
    /**
     * Bridger for moving ERC20 tokens back and forth betwen L1 to L2
     */
    constructor(l2Network) {
        super(l2Network);
    }
    /**
     * Get the address of the l1 gateway for this token
     * @param erc20L1Address
     * @param l1Provider
     * @returns
     */
    async getL1GatewayAddress(erc20L1Address, l1Provider) {
        await this.checkL1Network(l1Provider);
        return await L1GatewayRouter__factory_1.L1GatewayRouter__factory.connect(this.l2Network.tokenBridge.l1GatewayRouter, l1Provider).getGateway(erc20L1Address);
    }
    /**
     * Get the address of the l2 gateway for this token
     * @param erc20L1Address
     * @param l2Provider
     * @returns
     */
    async getL2GatewayAddress(erc20L1Address, l2Provider) {
        await this.checkL2Network(l2Provider);
        return await L2GatewayRouter__factory_1.L2GatewayRouter__factory.connect(this.l2Network.tokenBridge.l2GatewayRouter, l2Provider).getGateway(erc20L1Address);
    }
    /**
     * Approve tokens for deposit to the bridge. The tokens will be approved for the relevant gateway.
     * @param params
     * @returns
     */
    async approveToken(params) {
        if (!signerOrProvider_1.SignerProviderUtils.signerHasProvider(params.l1Signer)) {
            throw new errors_1.MissingProviderArbSdkError('l1Signer');
        }
        await this.checkL1Network(params.l1Signer);
        // you approve tokens to the gateway that the router will use
        const gatewayAddress = await this.getL1GatewayAddress(params.erc20L1Address, params.l1Signer.provider);
        const contract = await ERC20__factory_1.ERC20__factory.connect(params.erc20L1Address, params.l1Signer);
        return contract.functions.approve(gatewayAddress, params.amount || Erc20Bridger.MAX_APPROVAL, params.overrides || {});
    }
    /**
     * Get the L2 events created by a withdrawal
     * @param l2Provider
     * @param gatewayAddress
     * @param l1TokenAddress
     * @param fromAddress
     * @param filter
     * @returns
     */
    async getL2WithdrawalEvents(l2Provider, gatewayAddress, filter, l1TokenAddress, fromAddress) {
        await this.checkL2Network(l2Provider);
        const eventFetcher = new eventFetcher_1.EventFetcher(l2Provider);
        const events = (await eventFetcher.getEvents(gatewayAddress, L2ArbitrumGateway__factory_1.L2ArbitrumGateway__factory, contract => contract.filters.WithdrawalInitiated(null, fromAddress || null), filter)).map(a => (Object.assign({ txHash: a.transactionHash }, a.event)));
        return l1TokenAddress
            ? events.filter(log => log.l1Token.toLocaleLowerCase() ===
                l1TokenAddress.toLocaleLowerCase())
            : events;
    }
    /**
     * Does the provided address look like a weth gateway
     * @param potentialWethGatewayAddress
     * @param l1Provider
     * @returns
     */
    async looksLikeWethGateway(potentialWethGatewayAddress, l1Provider) {
        try {
            const potentialWethGateway = L1WethGateway__factory_1.L1WethGateway__factory.connect(potentialWethGatewayAddress, l1Provider);
            await potentialWethGateway.callStatic.l1Weth();
            return true;
        }
        catch (err) {
            if (err instanceof Error &&
                err.code ===
                    logger_1.Logger.errors.CALL_EXCEPTION) {
                return false;
            }
            else {
                throw err;
            }
        }
    }
    /**
     * Is this a known or unknown WETH gateway
     * @param gatewayAddress
     * @param l1Provider
     * @returns
     */
    async isWethGateway(gatewayAddress, l1Provider) {
        const wethAddress = this.l2Network.tokenBridge.l1WethGateway;
        if (this.l2Network.isCustom) {
            // For custom network, we do an ad-hoc check to see if it's a WETH gateway
            if (await this.looksLikeWethGateway(gatewayAddress, l1Provider)) {
                return true;
            }
            // ...otherwise we directly check it against the config file
        }
        else if (wethAddress === gatewayAddress) {
            return true;
        }
        return false;
    }
    /**
     * Get the L2 token contract at the provided address
     * Note: This function just returns a typed ethers object for the provided address, it doesnt
     * check the underlying form of the contract bytecode to see if it's an erc20, and doesn't ensure the validity
     * of any of the underlying functions on that contract.
     * @param l2Provider
     * @param l2TokenAddr
     * @returns
     */
    getL2TokenContract(l2Provider, l2TokenAddr) {
        return L2GatewayToken__factory_1.L2GatewayToken__factory.connect(l2TokenAddr, l2Provider);
    }
    /**
     * Get the L1 token contract at the provided address
     * Note: This function just returns a typed ethers object for the provided address, it doesnt
     * check the underlying form of the contract bytecode to see if it's an erc20, and doesn't ensure the validity
     * of any of the underlying functions on that contract.
     * @param l1Provider
     * @param l1TokenAddr
     * @returns
     */
    getL1TokenContract(l1Provider, l1TokenAddr) {
        return ERC20__factory_1.ERC20__factory.connect(l1TokenAddr, l1Provider);
    }
    /**
     * Get the corresponding L2 for the provided L1 token
     * @param erc20L1Address
     * @param l1Provider
     * @returns
     */
    async getL2ERC20Address(erc20L1Address, l1Provider) {
        await this.checkL1Network(l1Provider);
        const l1GatewayRouter = L1GatewayRouter__factory_1.L1GatewayRouter__factory.connect(this.l2Network.tokenBridge.l1GatewayRouter, l1Provider);
        return await l1GatewayRouter.functions
            .calculateL2TokenAddress(erc20L1Address)
            .then(([res]) => res);
    }
    /**
     * Get the corresponding L1 for the provided L2 token
     * Validates the returned address against the l2 router to ensure it is correctly mapped to the provided erc20L2Address
     * @param erc20L2Address
     * @param l2Provider
     * @returns
     */
    async getL1ERC20Address(erc20L2Address, l2Provider) {
        await this.checkL2Network(l2Provider);
        const arbERC20 = L2GatewayToken__factory_1.L2GatewayToken__factory.connect(erc20L2Address, l2Provider);
        const l1Address = await arbERC20.functions.l1Address().then(([res]) => res);
        // check that this l1 address is indeed registered to this l2 token
        const l2GatewayRouter = L2GatewayRouter__factory_1.L2GatewayRouter__factory.connect(this.l2Network.tokenBridge.l2GatewayRouter, l2Provider);
        const l2Address = await l2GatewayRouter.calculateL2TokenAddress(l1Address);
        if (l2Address.toLowerCase() !== erc20L2Address.toLowerCase()) {
            throw new errors_1.ArbSdkError(`Unexpected l1 address. L1 address from token is not registered to the provided l2 address. ${l1Address} ${l2Address} ${erc20L2Address}`);
        }
        return l1Address;
    }
    /**
     * Whether the token has been disabled on the router
     * @param l1TokenAddress
     * @param l1Provider
     * @returns
     */
    async l1TokenIsDisabled(l1TokenAddress, l1Provider) {
        await this.checkL1Network(l1Provider);
        const l1GatewayRouter = L1GatewayRouter__factory_1.L1GatewayRouter__factory.connect(this.l2Network.tokenBridge.l1GatewayRouter, l1Provider);
        return ((await l1GatewayRouter.l1TokenToGateway(l1TokenAddress)) ===
            constants_2.DISABLED_GATEWAY);
    }
    async validateDepositParams(params) {
        var _a;
        if (!signerOrProvider_1.SignerProviderUtils.signerHasProvider(params.l1Signer)) {
            throw new errors_1.MissingProviderArbSdkError('l1Signer');
        }
        await this.checkL1Network(params.l1Signer);
        await this.checkL2Network(params.l2Provider);
        if ((_a = params.overrides) === null || _a === void 0 ? void 0 : _a.value) {
            throw new errors_1.ArbSdkError('L1 call value should be set through l1CallValue param');
        }
    }
    async getDepositParams(params) {
        const { erc20L1Address, amount, l2Provider, l1Signer, destinationAddress } = params;
        const { retryableGasOverrides } = params;
        if (!signerOrProvider_1.SignerProviderUtils.signerHasProvider(l1Signer)) {
            throw new errors_1.MissingProviderArbSdkError('l1Signer');
        }
        // 1. get the params for a gas estimate
        const l1GatewayAddress = await this.getL1GatewayAddress(erc20L1Address, l1Signer.provider);
        const l1Gateway = L1ERC20Gateway__factory_1.L1ERC20Gateway__factory.connect(l1GatewayAddress, l1Signer.provider);
        const sender = await l1Signer.getAddress();
        const to = destinationAddress ? destinationAddress : sender;
        const depositCalldata = await l1Gateway.getOutboundCalldata(erc20L1Address, sender, to, amount, '0x');
        // The WETH gateway is the only deposit that requires callvalue in the L2 user-tx (i.e., the recently un-wrapped ETH)
        // Here we check if this is a WETH deposit, and include the callvalue for the gas estimate query if so
        const isWeth = await this.isWethGateway(l1GatewayAddress, l1Signer.provider);
        const estimateGasCallValue = isWeth ? amount : constants_1.Zero;
        const l2Dest = await l1Gateway.counterpartGateway();
        const gasEstimator = new L1ToL2MessageGasEstimator_1.L1ToL2MessageGasEstimator(l2Provider);
        let tokenGasOverrides = retryableGasOverrides;
        // we also add a hardcoded minimum gas limit for custom gateway deposits
        if (l1GatewayAddress === this.l2Network.tokenBridge.l1CustomGateway) {
            if (!tokenGasOverrides)
                tokenGasOverrides = {};
            if (!tokenGasOverrides.gasLimit)
                tokenGasOverrides.gasLimit = {};
            if (!tokenGasOverrides.gasLimit.min) {
                tokenGasOverrides.gasLimit.min =
                    Erc20Bridger.MIN_CUSTOM_DEPOSIT_GAS_LIMIT;
            }
        }
        // 2. get the gas estimates
        const baseFee = await (0, lib_1.getBaseFee)(l1Signer.provider);
        const excessFeeRefundAddress = sender;
        const callValueRefundAddress = sender;
        const estimates = await gasEstimator.estimateAll(l1GatewayAddress, l2Dest, depositCalldata, estimateGasCallValue, baseFee, excessFeeRefundAddress, callValueRefundAddress, l1Signer.provider, tokenGasOverrides);
        const data = abi_1.defaultAbiCoder.encode(['uint256', 'bytes'], [estimates.maxSubmissionFee, '0x']);
        return {
            l2GasLimit: estimates.gasLimit,
            maxSubmissionFee: estimates.maxSubmissionFee,
            l2MaxFeePerGas: estimates.maxFeePerGas,
            depositCallValue: estimates.totalL2GasCosts,
            destinationAddress: to,
            data: data,
            amount,
            erc20L1Address,
            retryableCallData: depositCalldata,
            retryableSender: l1GatewayAddress,
            retryableDestination: l2Dest,
            retryableExcessFeeRefundAddress: excessFeeRefundAddress,
            retryableCallValueRefundAddress: callValueRefundAddress,
            retryableValue: estimateGasCallValue,
        };
    }
    async depositTxOrGas(params, estimate) {
        await this.validateDepositParams(params);
        const depositParams = await this.getDepositParams(params);
        const l1GatewayRouter = L1GatewayRouter__factory_1.L1GatewayRouter__factory.connect(this.l2Network.tokenBridge.l1GatewayRouter, params.l1Signer);
        return await (estimate
            ? l1GatewayRouter.estimateGas
            : l1GatewayRouter.functions).outboundTransfer(depositParams.erc20L1Address, depositParams.destinationAddress, depositParams.amount, depositParams.l2GasLimit, depositParams.l2MaxFeePerGas, depositParams.data, Object.assign({ value: depositParams.depositCallValue }, params.overrides));
    }
    /**
     * Estimate the gas required for a token deposit
     * @param params
     * @returns
     */
    async depositEstimateGas(params) {
        return await this.depositTxOrGas(params, true);
    }
    /**
     * Execute a token deposit from L1 to L2
     * @param params
     * @returns
     */
    async deposit(params) {
        const tx = await this.depositTxOrGas(params, false);
        return L1Transaction_1.L1TransactionReceipt.monkeyPatchContractCallWait(tx);
    }
    async withdrawTxOrGas(params, estimate) {
        if (!signerOrProvider_1.SignerProviderUtils.signerHasProvider(params.l2Signer)) {
            throw new errors_1.MissingProviderArbSdkError('l2Signer');
        }
        await this.checkL2Network(params.l2Signer);
        const to = params.destinationAddress || (await params.l2Signer.getAddress());
        const l2GatewayRouter = L2GatewayRouter__factory_1.L2GatewayRouter__factory.connect(this.l2Network.tokenBridge.l2GatewayRouter, params.l2Signer);
        return (estimate ? l2GatewayRouter.estimateGas : l2GatewayRouter.functions)['outboundTransfer(address,address,uint256,bytes)'](params.erc20l1Address, to, params.amount, '0x', Object.assign({}, (params.overrides || {})));
    }
    /**
     * Estimate gas for withdrawing tokens from L2 to L1
     * @param params
     * @returns
     */
    async withdrawEstimateGas(params) {
        return this.withdrawTxOrGas(params, true);
    }
    /**
     * Withdraw tokens from L2 to L1
     * @param params
     * @returns
     */
    async withdraw(params) {
        const tx = await this.withdrawTxOrGas(params, false);
        return L2Transaction_1.L2TransactionReceipt.monkeyPatchWait(tx);
    }
}
exports.Erc20Bridger = Erc20Bridger;
Erc20Bridger.MAX_APPROVAL = constants_1.MaxUint256;
Erc20Bridger.MIN_CUSTOM_DEPOSIT_GAS_LIMIT = ethers_1.BigNumber.from(275000);
/**
 * Admin functionality for the token bridge
 */
class AdminErc20Bridger extends Erc20Bridger {
    /**
     * Register a custom token on the Arbitrum bridge
     * See https://developer.offchainlabs.com/docs/bridging_assets#the-arbitrum-generic-custom-gateway for more details
     * @param l1TokenAddress Address of the already deployed l1 token. Must inherit from https://developer.offchainlabs.com/docs/sol_contract_docs/md_docs/arb-bridge-peripherals/tokenbridge/ethereum/icustomtoken.
     * @param l2TokenAddress Address of the already deployed l2 token. Must inherit from https://developer.offchainlabs.com/docs/sol_contract_docs/md_docs/arb-bridge-peripherals/tokenbridge/arbitrum/iarbtoken.
     * @param l1Signer The signer with the rights to call registerTokenOnL2 on the l1 token
     * @param l2Provider Arbitrum rpc provider
     * @returns
     */
    async registerCustomToken(l1TokenAddress, l2TokenAddress, l1Signer, l2Provider) {
        if (!signerOrProvider_1.SignerProviderUtils.signerHasProvider(l1Signer)) {
            throw new errors_1.MissingProviderArbSdkError('l1Signer');
        }
        await this.checkL1Network(l1Signer);
        await this.checkL2Network(l2Provider);
        const l1SenderAddress = await l1Signer.getAddress();
        const l1Token = ICustomToken__factory_1.ICustomToken__factory.connect(l1TokenAddress, l1Signer);
        const l2Token = IArbToken__factory_1.IArbToken__factory.connect(l2TokenAddress, l2Provider);
        // sanity checks
        await l1Token.deployed();
        await l2Token.deployed();
        const l1AddressFromL2 = await l2Token.l1Address();
        if (l1AddressFromL2 !== l1TokenAddress) {
            throw new errors_1.ArbSdkError(`L2 token does not have l1 address set. Set address: ${l1AddressFromL2}, expected address: ${l1TokenAddress}.`);
        }
        const gasPriceEstimator = new L1ToL2MessageGasEstimator_1.L1ToL2MessageGasEstimator(l2Provider);
        // internally the registerTokenOnL2 sends two l1tol2 messages
        // the first registers the tokens and the second sets the gateways
        // we need to estimate gas for each of these l1tol2 messages
        // 1. registerTokenFromL1
        const il2CustomGateway = L2CustomGateway__factory_1.L2CustomGateway__factory.createInterface();
        const l2SetTokenCallData = il2CustomGateway.encodeFunctionData('registerTokenFromL1', [[l1TokenAddress], [l2TokenAddress]]);
        const l1SignerAddr = await l1Signer.getAddress();
        const baseFee = await (0, lib_1.getBaseFee)(l1Signer.provider);
        const setTokenEstimates = await gasPriceEstimator.estimateAll(this.l2Network.tokenBridge.l1CustomGateway, this.l2Network.tokenBridge.l2CustomGateway, l2SetTokenCallData, constants_1.Zero, baseFee, l1SignerAddr, l1SignerAddr, l1Signer.provider);
        // 2. setGateway
        const iL2GatewayRouter = L2GatewayRouter__factory_1.L2GatewayRouter__factory.createInterface();
        const l2SetGatewaysCallData = iL2GatewayRouter.encodeFunctionData('setGateway', [[l1TokenAddress], [this.l2Network.tokenBridge.l1CustomGateway]]);
        const setGatwayEstimates = await gasPriceEstimator.estimateAll(this.l2Network.tokenBridge.l1GatewayRouter, this.l2Network.tokenBridge.l2GatewayRouter, l2SetGatewaysCallData, constants_1.Zero, baseFee, l1SignerAddr, l1SignerAddr, l1Signer.provider);
        // now execute the registration
        const customRegistrationTx = await l1Token.registerTokenOnL2(l2TokenAddress, setTokenEstimates.maxSubmissionFee, setGatwayEstimates.maxSubmissionFee, setTokenEstimates.gasLimit, setGatwayEstimates.gasLimit, setGatwayEstimates.maxFeePerGas, setTokenEstimates.totalL2GasCosts, setGatwayEstimates.totalL2GasCosts, l1SenderAddress, {
            value: setTokenEstimates.totalL2GasCosts.add(setGatwayEstimates.totalL2GasCosts),
        });
        return L1Transaction_1.L1TransactionReceipt.monkeyPatchWait(customRegistrationTx);
    }
    /**
     * Get all the gateway set events on the L1 gateway router
     * @param l1Provider
     * @param customNetworkL1GatewayRouter
     * @returns
     */
    async getL1GatewaySetEvents(l1Provider, filter) {
        await this.checkL1Network(l1Provider);
        const l1GatewayRouterAddress = this.l2Network.tokenBridge.l1GatewayRouter;
        const eventFetcher = new eventFetcher_1.EventFetcher(l1Provider);
        return (await eventFetcher.getEvents(l1GatewayRouterAddress, L1GatewayRouter__factory_1.L1GatewayRouter__factory, t => t.filters.GatewaySet(), filter)).map(a => a.event);
    }
    /**
     * Get all the gateway set events on the L2 gateway router
     * @param l1Provider
     * @param customNetworkL1GatewayRouter
     * @returns
     */
    async getL2GatewaySetEvents(l2Provider, filter, customNetworkL2GatewayRouter) {
        if (this.l2Network.isCustom && !customNetworkL2GatewayRouter) {
            throw new errors_1.ArbSdkError('Must supply customNetworkL2GatewayRouter for custom network ');
        }
        await this.checkL2Network(l2Provider);
        const l2GatewayRouterAddress = customNetworkL2GatewayRouter || this.l2Network.tokenBridge.l2GatewayRouter;
        const eventFetcher = new eventFetcher_1.EventFetcher(l2Provider);
        return (await eventFetcher.getEvents(l2GatewayRouterAddress, L1GatewayRouter__factory_1.L1GatewayRouter__factory, t => t.filters.GatewaySet(), filter)).map(a => a.event);
    }
    /**
     * Register the provided token addresses against the provided gateways
     * @param l1Signer
     * @param l2Provider
     * @param tokenGateways
     * @returns
     */
    async setGateways(l1Signer, l2Provider, tokenGateways) {
        if (!signerOrProvider_1.SignerProviderUtils.signerHasProvider(l1Signer)) {
            throw new errors_1.MissingProviderArbSdkError('l1Signer');
        }
        await this.checkL1Network(l1Signer);
        await this.checkL2Network(l2Provider);
        const estimator = new L1ToL2MessageGasEstimator_1.L1ToL2MessageGasEstimator(l2Provider);
        const baseFee = await (0, lib_1.getBaseFee)(l1Signer.provider);
        const iL2GatewayRouter = L2GatewayRouter__factory_1.L2GatewayRouter__factory.createInterface();
        const l2SetGatewaysCallData = iL2GatewayRouter.encodeFunctionData('setGateway', [
            tokenGateways.map(tG => tG.tokenAddr),
            tokenGateways.map(tG => tG.gatewayAddr),
        ]);
        const l1SignerAddr = await l1Signer.getAddress();
        const estimates = await estimator.estimateAll(this.l2Network.tokenBridge.l1GatewayRouter, this.l2Network.tokenBridge.l2GatewayRouter, l2SetGatewaysCallData, constants_1.Zero, baseFee, l1SignerAddr, l1SignerAddr, l1Signer.provider);
        const l1GatewayRouter = L1GatewayRouter__factory_1.L1GatewayRouter__factory.connect(this.l2Network.tokenBridge.l1GatewayRouter, l1Signer);
        const res = await l1GatewayRouter.functions.setGateways(tokenGateways.map(tG => tG.tokenAddr), tokenGateways.map(tG => tG.gatewayAddr), estimates.gasLimit, estimates.maxFeePerGas, estimates.maxSubmissionFee, { value: estimates.totalL2GasCosts });
        return L1Transaction_1.L1TransactionReceipt.monkeyPatchContractCallWait(res);
    }
}
exports.AdminErc20Bridger = AdminErc20Bridger;
